import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  MdMoney,
  MdTrendingUp,
  MdTrendingDown,
  MdFilterList,
  MdEdit,
  MdDelete,
  MdClose,
  MdSave,
  MdWarning,
  MdInfoOutline,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardArrowDown,
  MdSearch,
  MdAccountBalanceWallet,
  MdPerson,
  MdCalendarToday,
  MdTask,
  MdSync,
  MdCreditCard,
} from "react-icons/md";

import { transactionsAPI, usersAPI, bankAccountsAPI } from "services/api";
import { useAuth } from "context/AuthContext";

// --- YARDIMCI FONKSİYONLAR ---
const formatCurrency = (value) => {
  return (Number(value) || 0).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
  });
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const dateObj = new Date(dateString);
    if (isNaN(dateObj)) return dateString;
    return dateObj.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (e) {
    return dateString;
  }
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${
    parts[parts.length - 1][0] || ""
  }`.toUpperCase();
};

const normalizeTr = (str) => {
  return str ? String(str).toLocaleLowerCase("tr-TR") : "";
};

const recurringMap = {
  daily: "Günlük",
  weekly: "Haftalık",
  monthly: "Aylık",
  yearly: "Yıllık",
};

// --- SKELETON LOADERS ---
const SummarySkeleton = () => (
  <div className="mt-4 grid animate-pulse grid-cols-1 gap-4 md:grid-cols-3">
    {[...Array(3)].map((_, i) => (
      <div
        key={i}
        className="h-28 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700"
      />
    ))}
  </div>
);

const ListSkeleton = () => (
  <div className="mt-6 flex animate-pulse flex-col gap-4">
    <div className="h-16 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="h-32 w-full rounded-[20px] bg-gray-100 dark:bg-navy-900/50"
        />
      ))}
    </div>
  </div>
);

// --- ANA BİLEŞEN ---
const AdminTransactionHistory = () => {
  const [allTransactions, setAllTransactions] = useState([]);
  const [staffMap, setStaffMap] = useState(new Map());
  const [staffList, setStaffList] = useState([]);
  const [bankMap, setBankMap] = useState(new Map());
  const [bankList, setBankList] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtreler (Varsayılan olarak Tümü)
  const [filters, setFilters] = useState({
    type: "all",
    source: "all",
    staff_id: "all",
    payment_method: "all",
    bank_account_id: "all",
    start_date: "",
    end_date: "",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false); // KURAL: Filtreler kapalı başlar

  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // Modal State
  const [modalConfig, setModalConfig] = useState({
    mode: "",
    data: null,
    error: null,
  });
  const [formLoading, setFormLoading] = useState(false);

  const { logout } = useAuth();

  // --- API FETCH ---
  useEffect(() => {
    let mounted = true;
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [transRes, staffRes, bankRes] = await Promise.all([
          transactionsAPI.getAll(),
          usersAPI.getStaff(),
          bankAccountsAPI.getAll(),
        ]);

        if (mounted && transRes.success) {
          const sortedTransactions = (transRes.transactions || []).sort(
            (a, b) => {
              const dateA = new Date(a.date).getTime();
              const dateB = new Date(b.date).getTime();
              if (dateA !== dateB) return dateB - dateA;
              return b.id - a.id;
            }
          );
          setAllTransactions(sortedTransactions);
        }

        if (mounted && staffRes.success) {
          const newMap = new Map();
          (staffRes.staff || []).forEach((s) =>
            newMap.set(s.id, `${s.name} ${s.surname}`)
          );
          setStaffMap(newMap);
          setStaffList(staffRes.staff || []);
        }

        if (mounted && bankRes.success) {
          const bMap = new Map();
          const banks = bankRes.bank_accounts || bankRes.accounts || [];
          banks.forEach((b) => bMap.set(b.id, b.name));
          setBankMap(bMap);
          setBankList(banks);
        }
      } catch (err) {
        console.error("Veri yükleme hatası:", err);
        if (String(err.message).includes("401")) {
          logout();
          return;
        }
        if (mounted) setError("Finans geçmişi yüklenirken bir hata oluştu.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchInitialData();
    return () => {
      mounted = false;
    };
  }, [logout]);

  // --- LOKAL FİLTRELEME VE ARAMA MANTIĞI ---
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter((tx) => {
      // 1. Kategori ve Tip Filtreleri
      if (filters.type !== "all" && tx.type !== filters.type) return false;
      if (filters.source === "manual" && tx.appointment_id) return false;
      if (filters.source === "appointment" && !tx.appointment_id) return false;
      if (
        filters.staff_id !== "all" &&
        String(tx.staff_id) !== String(filters.staff_id)
      )
        return false;
      if (
        filters.payment_method !== "all" &&
        tx.payment_method !== filters.payment_method
      )
        return false;
      if (
        filters.bank_account_id !== "all" &&
        String(tx.bank_account_id) !== String(filters.bank_account_id)
      )
        return false;

      // 2. Tarih Filtresi
      const txDate = tx.date ? tx.date.split("T")[0] : "";
      if (filters.start_date && txDate < filters.start_date) return false;
      if (filters.end_date && txDate > filters.end_date) return false;

      // 3. Metin Arama (Kusursuz Türkçe Karakter Desteği)
      if (searchQuery) {
        const q = normalizeTr(searchQuery);
        const title = normalizeTr(tx.title);
        const desc = normalizeTr(tx.description);
        const staffName = tx.staff_id
          ? normalizeTr(staffMap.get(tx.staff_id))
          : "";

        if (!title.includes(q) && !desc.includes(q) && !staffName.includes(q)) {
          return false;
        }
      }

      return true;
    });
  }, [allTransactions, filters, searchQuery, staffMap]);

  // --- DİNAMİK ÖZET HESAPLAMALARI (Filtreye Göre Anında Değişir) ---
  const summary = useMemo(() => {
    let income = 0;
    let expense = 0;
    filteredTransactions.forEach((tx) => {
      if (tx.type === "income") income += Number(tx.amount || 0);
      if (tx.type === "expense") expense += Number(tx.amount || 0);
    });
    return { income, expense, net: income - expense };
  }, [filteredTransactions]);

  // --- SAYFALAMA ---
  const totalPages = Math.max(
    1,
    Math.ceil(filteredTransactions.length / PAGE_SIZE)
  );
  const currentTransactions = filteredTransactions.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      type: "all",
      source: "all",
      staff_id: "all",
      payment_method: "all",
      bank_account_id: "all",
      start_date: "",
      end_date: "",
    });
    setSearchQuery("");
    setCurrentPage(1);
  };

  // --- MODAL İŞLEMLERİ ---
  const handleOpenEdit = (tx) => {
    setModalConfig({
      mode: "edit",
      data: {
        ...tx,
        amount: Number(tx.amount),
        payment_method: tx.payment_method || "cash",
        bank_account_id: tx.bank_account_id || "",
        date: tx.date ? tx.date.split("T")[0] : "",
      },
      error: null,
    });
  };

  const handleOpenDelete = (tx) => {
    setModalConfig({ mode: "delete", data: tx, error: null });
  };

  const handleCloseModal = () => {
    setModalConfig({ mode: "", data: null, error: null });
  };

  const handleModalInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setModalConfig((prev) => {
      let newData = {
        ...prev.data,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "is_recurring" && !checked) newData.recurring_type = "";
      if (name === "payment_method" && value === "cash")
        newData.bank_account_id = "";

      return { ...prev, data: newData };
    });
  };

  const handleModalSave = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setModalConfig((prev) => ({ ...prev, error: null }));
    const tx = modalConfig.data;

    if (tx.is_recurring && !tx.recurring_type) {
      setFormLoading(false);
      return setModalConfig((prev) => ({
        ...prev,
        error: "Lütfen yinelenme tipini seçin.",
      }));
    }
    if (
      (tx.payment_method === "card" || tx.payment_method === "transfer") &&
      !tx.bank_account_id
    ) {
      setFormLoading(false);
      return setModalConfig((prev) => ({
        ...prev,
        error: "Lütfen bir banka hesabı seçin.",
      }));
    }

    try {
      const payload = {
        ...tx,
        staff_id: tx.staff_id || null,
        bank_account_id:
          tx.payment_method === "cash" ? null : tx.bank_account_id,
        amount: parseFloat(tx.amount),
      };

      const res = await transactionsAPI.update(tx.id, payload);
      if (res.success || res.transaction) {
        setAllTransactions((prev) =>
          prev
            .map((item) =>
              item.id === tx.id ? res.transaction || payload : item
            )
            .sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            )
        );
        handleCloseModal();
      }
    } catch (err) {
      setModalConfig((prev) => ({
        ...prev,
        error: err.response?.data?.message || "Güncelleme hatası.",
      }));
    } finally {
      setFormLoading(false);
    }
  };

  const handleModalDelete = async () => {
    setFormLoading(true);
    const txId = modalConfig.data.id;
    try {
      await transactionsAPI.delete(txId);
      setAllTransactions((prev) => prev.filter((item) => item.id !== txId));
      handleCloseModal();
    } catch (err) {
      setModalConfig((prev) => ({ ...prev, error: "Silme işlemi başarısız." }));
    } finally {
      setFormLoading(false);
    }
  };

  // --- GÖRÜNÜM (RENDER) ---
  return (
    <div className="animate-fadeIn mt-5 flex w-full flex-col gap-6 pb-20">
      {/* 1. BAŞLIK VE FİLTRE AÇ/KAPAT */}
      <div className="flex flex-col items-center justify-between gap-4 rounded-[20px] border border-gray-100 bg-white p-6 shadow-3xl shadow-shadow-500 dark:border-white/10 dark:bg-navy-800 dark:shadow-none sm:flex-row">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
            <MdMoney className="text-3xl" />
          </div>
          <div>
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              İşlem Geçmişi
            </h4>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Gerçekleşen tüm finansal hareketleri listeleyin ve yönetin.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex h-11 items-center justify-center gap-2 rounded-xl px-6 text-sm font-bold transition-all ${
            showFilters
              ? "bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-900 dark:text-gray-300 dark:hover:bg-navy-700"
          }`}
        >
          <MdFilterList className="text-lg" />{" "}
          {showFilters ? "Filtreleri Gizle" : "Filtrele & Ara"}
        </button>
      </div>

      {/* 2. DİNAMİK ÖZET İSTATİSTİKLERİ */}
      {loading ? (
        <SummarySkeleton />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="relative flex items-center justify-between overflow-hidden rounded-[20px] border border-gray-100 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
            <div className="z-10">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-widest text-gray-500">
                Görüntülenen Gelir
              </p>
              <h4 className="text-2xl font-black text-green-500">
                {formatCurrency(summary.income)}
              </h4>
            </div>
            <div className="z-10 flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-500 dark:bg-green-500/10 dark:text-green-400">
              <MdTrendingUp className="text-2xl" />
            </div>
          </div>

          <div className="relative flex items-center justify-between overflow-hidden rounded-[20px] border border-gray-100 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
            <div className="z-10">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-widest text-gray-500">
                Görüntülenen Gider
              </p>
              <h4 className="text-2xl font-black text-red-500">
                {formatCurrency(summary.expense)}
              </h4>
            </div>
            <div className="z-10 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-400">
              <MdTrendingDown className="text-2xl" />
            </div>
          </div>

          <div className="relative flex items-center justify-between overflow-hidden rounded-[20px] bg-gradient-to-br from-brand-500 to-brand-700 p-5 text-white shadow-lg shadow-brand-500/30">
            <div className="z-10">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-widest text-white/80">
                Net Durum
              </p>
              <h4 className="text-3xl font-black">
                {formatCurrency(summary.net)}
              </h4>
            </div>
            <div className="z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-md">
              <MdAccountBalanceWallet className="text-2xl" />
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400">
          <MdWarning className="text-xl" /> {error}
        </div>
      )}

      {/* 3. FİLTRELEME ALANI (Sadece toggle açıksa görünür) */}
      <div
        className={`overflow-hidden transition-all duration-300 ${
          showFilters ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="mb-2 rounded-[20px] border border-gray-100 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="xl:col-span-2">
              <label className="mb-1 ml-1 block text-[10px] font-bold uppercase text-gray-400">
                Metin Arama (Başlık, Açıklama veya Personel)
              </label>
              <div className="relative">
                <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Ara..."
                  className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm font-medium text-navy-700 outline-none transition-all focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </div>
            </div>

            <div className="flex gap-2 xl:col-span-2">
              <div className="flex-1">
                <label className="mb-1 ml-1 block text-[10px] font-bold uppercase text-gray-400">
                  Başlangıç
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                  className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-bold text-navy-700 outline-none dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </div>
              <div className="flex-1">
                <label className="mb-1 ml-1 block text-[10px] font-bold uppercase text-gray-400">
                  Bitiş
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                  className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-bold text-navy-700 outline-none dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 ml-1 block text-[10px] font-bold uppercase text-gray-400">
                İşlem Tipi
              </label>
              <div className="relative">
                <select
                  name="type"
                  value={filters.type}
                  onChange={handleFilterChange}
                  className="h-10 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-bold text-navy-700 outline-none dark:border-white/10 dark:bg-navy-900 dark:text-white"
                >
                  <option value="all">Tümü</option>
                  <option value="income">Gelir (+)</option>
                  <option value="expense">Gider (-)</option>
                </select>
                <MdKeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="mb-1 ml-1 block text-[10px] font-bold uppercase text-gray-400">
                Ödeme Yöntemi
              </label>
              <div className="relative">
                <select
                  name="payment_method"
                  value={filters.payment_method}
                  onChange={handleFilterChange}
                  className="h-10 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-bold text-navy-700 outline-none dark:border-white/10 dark:bg-navy-900 dark:text-white"
                >
                  <option value="all">Tümü</option>
                  <option value="cash">Nakit</option>
                  <option value="card">Kart / Banka</option>
                </select>
                <MdKeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="mb-1 ml-1 block text-[10px] font-bold uppercase text-gray-400">
                Kaynak
              </label>
              <div className="relative">
                <select
                  name="source"
                  value={filters.source}
                  onChange={handleFilterChange}
                  className="h-10 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-bold text-navy-700 outline-none dark:border-white/10 dark:bg-navy-900 dark:text-white"
                >
                  <option value="all">Tümü</option>
                  <option value="manual">Manuel Eklenenler</option>
                  <option value="appointment">Randevu Gelirleri</option>
                </select>
                <MdKeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="h-10 w-full rounded-xl bg-red-50 text-sm font-bold text-red-500 transition-all hover:bg-red-500 hover:text-white dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white"
              >
                Filtreleri Temizle
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 4. İŞLEMLER LİSTESİ */}
      <div className="rounded-[20px] border border-gray-100 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        {loading ? (
          <ListSkeleton />
        ) : filteredTransactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 opacity-60">
            <MdMoney className="mb-4 text-6xl text-gray-300 dark:text-gray-600" />
            <h5 className="text-xl font-bold text-navy-700 dark:text-white">
              İşlem Bulunamadı
            </h5>
            <p className="mt-1 text-sm font-medium text-gray-500">
              Seçili filtrelere uygun finansal hareket yok.
            </p>
          </div>
        ) : (
          <>
            {/* Masaüstü Tablo */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-100 text-[11px] font-bold uppercase tracking-widest text-gray-400 dark:border-white/10">
                    <th className="w-[250px] py-3 pl-4">Başlık & Açıklama</th>
                    <th className="py-3">Tutar</th>
                    <th className="py-3">Ödeme / Kaynak</th>
                    <th className="py-3">Tarih</th>
                    <th className="py-3">Personel</th>
                    <th className="py-3 pr-4 text-right">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTransactions.map((tx) => {
                    const isIncome = tx.type === "income";
                    const isManual = !tx.appointment_id;
                    const isRecurring = tx.is_recurring && tx.recurring_type;
                    const isCard =
                      tx.payment_method === "card" ||
                      tx.payment_method === "transfer";
                    const bankName =
                      isCard && tx.bank_account_id
                        ? bankMap.get(tx.bank_account_id)
                        : null;

                    return (
                      <tr
                        key={tx.id}
                        className="border-b border-gray-50 transition-colors hover:bg-gray-50/50 dark:border-white/5 dark:hover:bg-white/5"
                      >
                        <td className="py-3 pl-4">
                          <h6 className="line-clamp-1 text-sm font-bold text-navy-700 dark:text-white">
                            {tx.title}
                          </h6>
                          {/* Açıklama Hatası Çözüldü */}
                          <p className="mt-1 line-clamp-2 text-xs font-medium italic text-gray-500 dark:text-gray-400">
                            {tx.description ||
                              (isManual ? "Manuel İşlem" : "Randevu Geliri")}
                          </p>
                        </td>
                        <td className="py-3">
                          <span
                            className={`text-sm font-black ${
                              isIncome ? "text-green-500" : "text-red-500"
                            }`}
                          >
                            {isIncome ? "+" : "-"}
                            {formatCurrency(tx.amount)}
                          </span>
                        </td>
                        <td className="py-3">
                          <div className="flex flex-col gap-1">
                            <span className="flex items-center gap-1 text-xs font-bold text-navy-700 dark:text-white">
                              {isCard ? (
                                <>
                                  <MdCreditCard className="text-info" />{" "}
                                  {bankName || "Kart"}
                                </>
                              ) : (
                                <>
                                  <MdMoney className="text-success" /> Nakit
                                </>
                              )}
                            </span>
                            <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-gray-400">
                              {isManual ? <MdTask /> : <MdCalendarToday />}{" "}
                              {isManual ? "Manuel" : "Randevu"}
                              {isRecurring && (
                                <span className="ml-1 text-amber-500">
                                  ({recurringMap[tx.recurring_type]})
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        {/* Tarih formatı hatası çözüldü */}
                        <td className="py-3 text-sm font-medium text-gray-600 dark:text-gray-400">
                          {formatDate(tx.date)}
                        </td>
                        <td className="py-3">
                          {tx.staff_id ? (
                            <div className="flex items-center gap-2">
                              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500 text-[10px] font-bold text-white">
                                {getInitials(staffMap.get(tx.staff_id))}
                              </div>
                              <span className="line-clamp-1 text-xs font-bold text-navy-700 dark:text-white">
                                {staffMap.get(tx.staff_id)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenEdit(tx)}
                              className="rounded-lg bg-gray-50 p-2 text-gray-500 transition-all hover:bg-brand-50 hover:text-brand-500 dark:bg-navy-900 dark:hover:bg-brand-400/10 dark:hover:text-brand-400"
                            >
                              <MdEdit />
                            </button>
                            <button
                              onClick={() => handleOpenDelete(tx)}
                              className="rounded-lg bg-red-50 p-2 text-red-500 transition-all hover:bg-red-500 hover:text-white dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobil Kart Görünümü */}
            <div className="grid grid-cols-1 gap-4 md:hidden">
              {currentTransactions.map((tx) => {
                const isIncome = tx.type === "income";
                const isManual = !tx.appointment_id;
                const isRecurring = tx.is_recurring && tx.recurring_type;
                const isCard =
                  tx.payment_method === "card" ||
                  tx.payment_method === "transfer";
                const bankName =
                  isCard && tx.bank_account_id
                    ? bankMap.get(tx.bank_account_id)
                    : null;

                return (
                  <div
                    key={tx.id}
                    className="flex flex-col rounded-[20px] border border-gray-100 bg-gray-50/50 p-4 dark:border-white/10 dark:bg-navy-900/30"
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div className="flex w-[70%] items-center gap-3">
                        <div
                          className={`flex h-10 w-10 flex-none items-center justify-center rounded-xl font-bold shadow-sm ${
                            isIncome
                              ? "bg-green-50 text-green-500 dark:bg-green-500/10"
                              : "bg-red-50 text-red-500 dark:bg-red-500/10"
                          }`}
                        >
                          {isIncome ? <MdTrendingUp /> : <MdTrendingDown />}
                        </div>
                        <div className="overflow-hidden">
                          <h6 className="truncate text-sm font-bold text-navy-700 dark:text-white">
                            {tx.title}
                          </h6>
                          <span className="text-xs font-medium text-gray-500">
                            {formatDate(tx.date)}
                          </span>
                        </div>
                      </div>
                      <span
                        className={`text-base font-black ${
                          isIncome ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        {isIncome ? "+" : "-"}
                        {formatCurrency(tx.amount)}
                      </span>
                    </div>

                    {/* Açıklama Hatası Çözüldü (Mobil) */}
                    <div className="mb-3 px-1">
                      <p className="line-clamp-2 text-[11px] font-medium italic text-gray-600 dark:text-gray-300">
                        "
                        {tx.description ||
                          (isManual ? "Manuel İşlem" : "Randevu Geliri")}
                        "
                      </p>
                    </div>

                    <div className="mb-4 grid grid-cols-2 gap-2">
                      <div className="rounded-xl border border-gray-100 bg-white p-2 dark:border-white/5 dark:bg-navy-800">
                        <span className="mb-1 block text-[10px] font-bold uppercase text-gray-400">
                          Ödeme Yöntemi
                        </span>
                        <span className="flex items-center gap-1 text-xs font-bold text-navy-700 dark:text-white">
                          {isCard ? (
                            <>
                              <MdCreditCard className="text-info" />{" "}
                              {bankName || "Kart"}
                            </>
                          ) : (
                            <>
                              <MdMoney className="text-success" /> Nakit
                            </>
                          )}
                        </span>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-white p-2 dark:border-white/5 dark:bg-navy-800">
                        <span className="mb-1 block text-[10px] font-bold uppercase text-gray-400">
                          Kaynak
                        </span>
                        <span className="flex items-center gap-1 truncate text-xs font-bold text-navy-700 dark:text-white">
                          {isManual ? <MdTask /> : <MdCalendarToday />}{" "}
                          {isManual ? "Manuel" : "Randevu"}
                        </span>
                      </div>
                      {tx.staff_id && (
                        <div className="col-span-2 rounded-xl border border-brand-100 bg-brand-50 p-2 dark:border-brand-400/20 dark:bg-brand-400/10">
                          <span className="mb-1 block text-[10px] font-bold uppercase text-brand-500 dark:text-brand-400">
                            İlgili Personel
                          </span>
                          <span className="flex items-center gap-1 truncate text-xs font-bold text-brand-700 dark:text-brand-300">
                            <MdPerson /> {staffMap.get(tx.staff_id)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 border-t border-gray-100 pt-3 dark:border-white/10">
                      <button
                        onClick={() => handleOpenEdit(tx)}
                        className="h-8 rounded-lg border border-gray-200 bg-white px-3 text-xs font-bold text-gray-600 dark:border-white/10 dark:bg-navy-800 dark:text-gray-300"
                      >
                        Düzenle
                      </button>
                      <button
                        onClick={() => handleOpenDelete(tx)}
                        className="h-8 rounded-lg bg-red-50 px-3 text-xs font-bold text-red-500 dark:bg-red-500/10 dark:text-red-400"
                      >
                        Sil
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* SAYFALAMA */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4 dark:border-white/10">
                <p className="text-sm font-medium text-gray-500">
                  <span className="font-bold text-navy-700 dark:text-white">
                    {filteredTransactions.length}
                  </span>{" "}
                  kayıttan{" "}
                  <span className="font-bold text-navy-700 dark:text-white">
                    {(currentPage - 1) * PAGE_SIZE + 1}-
                    {Math.min(
                      currentPage * PAGE_SIZE,
                      filteredTransactions.length
                    )}
                  </span>{" "}
                  gösteriliyor.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-all hover:bg-gray-50 disabled:opacity-40 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700"
                  >
                    <MdKeyboardArrowLeft className="text-xl" />
                  </button>
                  <div className="flex h-9 min-w-[36px] items-center justify-center rounded-lg bg-brand-500 px-2 text-sm font-bold text-white shadow-md">
                    {currentPage}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-all hover:bg-gray-50 disabled:opacity-40 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700"
                  >
                    <MdKeyboardArrowRight className="text-xl" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 5. DÜZENLEME MODALI */}
      {modalConfig.mode === "edit" && (
        <div className="animate-fadeIn fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/40 p-4 backdrop-blur-sm transition-all">
          <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl dark:bg-navy-800">
            <div className="flex items-center justify-between border-b border-gray-100 p-6 dark:border-white/10">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
                  <MdEdit className="text-xl" />
                </div>
                <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                  İşlemi Düzenle
                </h3>
              </div>
              <button
                onClick={handleCloseModal}
                className="rounded-full p-2 text-gray-500 transition-all hover:bg-gray-100 dark:hover:bg-navy-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="custom-modal-scroll flex-1 overflow-y-auto p-6">
              {modalConfig.error && (
                <div className="mb-6 flex items-center gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400">
                  <MdWarning className="flex-none text-xl" />
                  <span className="text-sm font-bold">{modalConfig.error}</span>
                </div>
              )}

              <form
                id="editForm"
                onSubmit={handleModalSave}
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 ml-1 block text-sm font-bold text-navy-700 dark:text-white">
                      Başlık *
                    </label>
                    <input
                      required
                      name="title"
                      value={modalConfig.data.title}
                      onChange={handleModalInputChange}
                      className="flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none transition-all focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400"
                    />
                  </div>
                  <div>
                    <label className="mb-2 ml-1 block text-sm font-bold text-navy-700 dark:text-white">
                      Tutar *
                    </label>
                    <input
                      required
                      type="number"
                      step="0.01"
                      name="amount"
                      value={modalConfig.data.amount}
                      onChange={handleModalInputChange}
                      className="flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none transition-all focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 ml-1 block text-sm font-bold text-navy-700 dark:text-white">
                      İşlem Tipi
                    </label>
                    <div className="relative">
                      <select
                        name="type"
                        value={modalConfig.data.type}
                        onChange={handleModalInputChange}
                        className="h-12 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white/0 px-3 text-sm font-bold text-navy-700 outline-none transition-all focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                      >
                        <option value="income">Gelir (+)</option>
                        <option value="expense">Gider (-)</option>
                      </select>
                      <MdKeyboardArrowDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 ml-1 block text-sm font-bold text-navy-700 dark:text-white">
                      Tarih *
                    </label>
                    <input
                      required
                      type="date"
                      name="date"
                      value={modalConfig.data.date}
                      onChange={handleModalInputChange}
                      className="flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none transition-all focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 ml-1 block text-sm font-bold text-navy-700 dark:text-white">
                      Ödeme Yöntemi
                    </label>
                    <div className="relative">
                      <select
                        name="payment_method"
                        value={modalConfig.data.payment_method}
                        onChange={handleModalInputChange}
                        className="h-12 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white/0 px-3 text-sm font-bold text-navy-700 outline-none transition-all focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                      >
                        <option value="cash">Nakit</option>
                        <option value="card">Kart / Banka</option>
                      </select>
                      <MdKeyboardArrowDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 ml-1 block text-sm font-bold text-navy-700 dark:text-white">
                      Banka Hesabı
                    </label>
                    <div className="relative">
                      <select
                        name="bank_account_id"
                        value={modalConfig.data.bank_account_id}
                        onChange={handleModalInputChange}
                        disabled={modalConfig.data.payment_method === "cash"}
                        className="h-12 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-bold text-navy-700 outline-none transition-all focus:border-brand-500 disabled:opacity-50 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                      >
                        <option value="">Seçiniz...</option>
                        {bankList.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                      <MdKeyboardArrowDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="mb-2 ml-1 block text-sm font-bold text-navy-700 dark:text-white">
                      İlgili Personel
                    </label>
                    <div className="relative">
                      <select
                        name="staff_id"
                        value={modalConfig.data.staff_id || ""}
                        onChange={handleModalInputChange}
                        className="h-12 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white/0 px-3 text-sm font-bold text-navy-700 outline-none transition-all focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                      >
                        <option value="">Personel Bağlantısı Yok</option>
                        {staffList.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name} {s.surname}
                          </option>
                        ))}
                      </select>
                      <MdKeyboardArrowDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="mb-2 ml-1 block text-sm font-bold text-navy-700 dark:text-white">
                    Açıklama
                  </label>
                  <textarea
                    name="description"
                    value={modalConfig.data.description || ""}
                    onChange={handleModalInputChange}
                    rows="3"
                    className="flex w-full resize-none items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm font-medium text-navy-700 outline-none transition-all focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400"
                    placeholder="İşlem hakkında detaylar..."
                  />
                </div>
              </form>
            </div>

            <div className="flex flex-none items-center justify-end gap-3 border-t border-gray-100 bg-gray-50 p-6 dark:border-white/10 dark:bg-navy-800/50">
              <button
                onClick={handleCloseModal}
                className="h-11 rounded-xl px-6 text-sm font-bold text-gray-600 transition-all hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                İptal
              </button>
              <button
                type="submit"
                form="editForm"
                disabled={formLoading}
                className="flex h-11 items-center justify-center gap-2 rounded-xl bg-brand-500 px-8 text-sm font-bold text-white shadow-lg shadow-brand-500/30 transition-all hover:bg-brand-600 active:scale-95 disabled:opacity-60"
              >
                {formLoading ? (
                  <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white" />
                ) : (
                  <>
                    <MdSave className="text-lg" /> Güncelle
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 6. SİLME MODALI */}
      {modalConfig.mode === "delete" && (
        <div className="animate-fadeIn fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/40 p-4 backdrop-blur-sm transition-all">
          <div className="relative w-full max-w-md rounded-[24px] bg-white p-6 shadow-2xl dark:bg-navy-800">
            <div className="mb-4 flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-400">
                <MdWarning className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                İşlemi Sil
              </h3>
            </div>
            <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">
              <strong>"{modalConfig.data.title}"</strong> başlıklı işlemi kalıcı
              olarak silmek istediğinize emin misiniz?
            </p>
            {modalConfig.error && (
              <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm font-bold text-red-600 dark:bg-red-500/10 dark:text-red-400">
                {modalConfig.error}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="h-11 rounded-xl px-6 text-sm font-bold text-gray-600 transition-all hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                İptal
              </button>
              <button
                onClick={handleModalDelete}
                disabled={formLoading}
                className="flex h-11 items-center justify-center rounded-xl bg-red-500 px-8 text-sm font-bold text-white shadow-lg shadow-red-500/30 transition-all hover:bg-red-600 active:scale-95 disabled:opacity-60"
              >
                {formLoading ? (
                  <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white" />
                ) : (
                  "Evet, Sil"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ekstra CSS (Scrollbar) */}
      <style jsx="true">{`
        .custom-modal-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .custom-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(163, 174, 208, 0.3);
          border-radius: 10px;
        }
        .dark .custom-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
};

export default AdminTransactionHistory;
